import React, { useEffect, useRef, useState } from "react";

import {
  CalendarIcon,
  ChevronLeft,
  Loader2,
  Users,
  Pencil,
} from "lucide-react";

import { format, addDays } from "date-fns";
import { parseDateOnly } from "@/utils/parseDateOnly";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import AuthGateModal from "../../components/AuthGateModal";
import { createBooking, newBookingId } from "@/services/bookingService";
import StripePaymentSection from "@/components/paymentSection/Stripepaymentsection";
import { getCouponByCode, validateCoupon } from "../../services/couponService";
import { useSiteContent } from "@/cms/useSiteContent";
import { useSiteTexts } from "@/cms/useSiteText";
import {
  calculateGolfTotal,
  calculateFerryTotal,
  calculateTransportTotal,
  calculateAccommodationTotal,
  calculateFinalCourseRoundPrice,
  deriveTeePeriod,
  toDateKey,
} from "@/utils/pricingEngine";

/* ── Draft booking ID persistence ──────────────────────────────────────────
 * One unpaid draft per user per checkout session. The ID is remembered in
 * sessionStorage so Back / Edit golfers / refresh all reuse the same booking
 * instead of creating a new one each time. Cleared once payment succeeds. */
const draftKeyFor = (uid) => `exqura:draftBookingId:${uid}`;
const readDraftId = (uid) => {
  try {
    return sessionStorage.getItem(draftKeyFor(uid));
  } catch {
    return null;
  }
};
const saveDraftId = (uid, id) => {
  try {
    sessionStorage.setItem(draftKeyFor(uid), id);
  } catch {
    /* ignore */
  }
};
const clearDraftId = (uid) => {
  try {
    sessionStorage.removeItem(draftKeyFor(uid));
  } catch {
    /* ignore */
  }
};

/**
 * @param {{
 *   golfers: Array<{
 *     name: string,
 *     passportNumber: string,
 *     phone: string,
 *     email: string,
 *     isLead: boolean,
 *   }>; // finalized roster handed off from GolferDetailsPage
 *   onEditGolfers?: () => void; // -> back to GolferDetailsPage
 * }} extra props, alongside the existing ones below
 */
export default function PackageCheckoutView({
  selectedCourses,
  selectedAccommodation,
  sharingType,
  numRooms,
  checkInDate,
  checkInTime,
  checkOutDate,
  selectedFerry,
  selectedFerryTime,
  selectedOutboundFerryTime,
  selectedTransport,
  selectedTransportTime,
  selectedOutboundTransport,
  selectedOutboundTransportTime,
  selectedPackage,
  numPlayers,
  roundDates,
  roundTimes,
  startDate,
  endDate,
  golfers,
  bookingId: bookingIdProp,
  onBookingCreated,
  onEditGolfers,
  onConfirm,
  onBack,
}) {
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [user, setUser] = useState(auth.currentUser);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [bookingId, setBookingId] = useState(null); // set only after the draft is saved
  const [savedTotal, setSavedTotal] = useState(null); // grandTotal the saved draft holds

  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const draftIdRef = useRef(null);
  const saveChainRef = useRef(Promise.resolve());

  // Re-check auth (defense in depth) and keep `user` in React state so the
  // draft effect reacts to sign-in.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (!u) setShowAuthGate(true);
    });
    return unsubscribe;
  }, []);

  // Admin-editable footnote under the Golf Rounds pricing — see the
  // "checkout" page in the Edit Site CMS.
  const { blocks: checkoutBlocks } = useSiteContent("checkout");
  const golfFootnote = checkoutBlocks.find(
    (b) => b.type === "checkout-footnotes",
  )?.fields?.text;

  const t = useSiteTexts({
    title: "checkout.title",
    subtitle: "checkout.subtitle",
    sectionGolfRounds: "checkout.sectionGolfRounds",
    sectionFerry: "checkout.sectionFerry",
    sectionLandTransport: "checkout.sectionLandTransport",
    sectionAccommodation: "checkout.sectionAccommodation",
    couponHeading: "checkout.couponHeading",
    couponSubtext: "checkout.couponSubtext",
    couponPlaceholder: "checkout.couponPlaceholder",
    couponApplyButton: "checkout.couponApplyButton",
    couponHelpText: "checkout.couponHelpText",
    summaryLandTransportLabel: "checkout.summaryLandTransportLabel",
    totalLabel: "checkout.totalLabel",
    preparingBooking: "checkout.preparingBooking",
    errorBookingStart: "checkout.errorBookingStart",
    errorPaymentFailed: "checkout.errorPaymentFailed",
  });

  /* ── Pricing ── */
  const nights = selectedPackage?.nights ?? 1;
  const players = numPlayers || 1;
  const numRounds = selectedCourses.length;
  const anchorCourse = selectedCourses[0];

  const golfRounds = selectedCourses.map((course, i) => ({
    course,
    date: roundDates?.[i],
    period: deriveTeePeriod(roundTimes?.[i]),
  }));
  const golfTotal = calculateGolfTotal({
    rounds: golfRounds,
    totalGuests: players,
  });

  // Per-round price (per player)
  const roundPrices = golfRounds.map((r) =>
    r.date
      ? calculateFinalCourseRoundPrice({
          course: r.course,
          date: r.date,
          period: r.period,
          totalPlayers: players,
          totalRounds: numRounds,
        })
      : null,
  );

  const nightDates = checkInDate
    ? Array.from({ length: nights }, (_, i) =>
        addDays(parseDateOnly(checkInDate), i),
      )
    : [];
  const roundPeriodByDate = Object.fromEntries(
    (roundDates || []).map((d, i) => [
      toDateKey(d),
      deriveTeePeriod(roundTimes?.[i]) || "AM",
    ]),
  );

  // Rooms booked — bookings from before the room selector default to one
  // room per player.
  const rooms = numRooms || players;

  const accommodationTotal = selectedAccommodation
    ? calculateAccommodationTotal({
        accommodation: selectedAccommodation,
        nightDates,
        sharingType,
        numRooms: rooms,
        totalGuests: players,
        roundPeriodByDate,
      }).total
    : 0;

  // Round-trip ferry: same route/price for the inbound and outbound legs.
  const ferryTotal = selectedFerry
    ? calculateFerryTotal({ ferry: selectedFerry, totalGuests: players })
    : 0;

  const inboundPeriod = deriveTeePeriod(roundTimes?.[0]);

  const transportTotal = selectedTransport
    ? calculateTransportTotal({
        course: anchorCourse,
        vehicle: selectedTransport,
        totalGuests: players,
        numRounds,
        date: roundDates?.[0],
        period: inboundPeriod,
      }).total
    : 0;
  // Which subtotal a coupon's % applies against, based on its scope.
  const scopeSubtotal = (scope) => {
    switch (scope) {
      case "gateway":
        return golfTotal;
      case "ferry":
        return ferryTotal;
      case "transport":
        return transportTotal;
      case "accommodation":
        return accommodationTotal;
      case "global":
      default:
        return golfTotal + accommodationTotal + ferryTotal + transportTotal;
    }
  };

  const discountAmount = appliedCoupon
    ? Math.round(
        (scopeSubtotal(appliedCoupon.scope) *
          (Number(appliedCoupon.discountPercent) || 0)) /
          100,
      )
    : 0;

  const grandTotal =
    golfTotal +
    accommodationTotal +
    ferryTotal +
    transportTotal -
    discountAmount;

  const couponCode = appliedCoupon?.code || null;

  /* ── Booking record ── */
  const buildBookingRecord = async () => {
    const roster = golfers || [];
    return {
      userId: auth.currentUser?.uid,
      customerEmail: auth.currentUser?.email || null,
      customerName: auth.currentUser?.displayName || null,
      selectedCourses,
      roundDates,
      roundTimes,
      selectedAccommodation,
      sharingType,
      numRooms: selectedAccommodation ? rooms : null,
      checkInDate,
      checkInTime,
      checkOutDate,
      selectedFerry,
      selectedFerryTime,
      selectedOutboundFerryTime,
      selectedTransport,
      selectedTransportTime,
      selectedOutboundTransport,
      selectedOutboundTransportTime,
      selectedPackage,
      numPlayers: players,
      // Display names only — index 0 is always the lead golfer.
      golferNames: roster.map((g) => g.name),
      // Full roster collected on the Golfer Details page.
      golferDetails: roster.map((g) => ({
        name: g.name,
        passportNumber: g.passportNumber,
        phone: g.phone,
        email: g.email,
        isLead: !!g.isLead,
      })),
      appliedCoupon: appliedCoupon
        ? {
            code: appliedCoupon.code,
            scope: appliedCoupon.scope,
            discountPercent: appliedCoupon.discountPercent,
          }
        : null,
      discountAmount,
      grandTotal,
      // Itemized subtotals — persisted so admin/reporting can show an
      // accurate breakdown later without recomputing.
      priceBreakdown: {
        golfTotal,
        accommodationTotal,
        ferryTotal,
        transportTotal,
      },
      // Payment is handled by Stripe; the webhook flips these once
      // payment_intent.succeeded actually arrives.
      status: "pending_payment",
      paymentStatus: "unpaid",
      createdAt: new Date().toISOString(),
    };
  };

  /* ── Draft save ──────────────────────────────────────────────────────────
   * Creates the draft once, then re-saves the SAME booking (same ID) whenever
   * the total or coupon changes, so Firestore and the Stripe PaymentIntent
   * always match what's on screen. Saves are chained so rapid changes can't
   * land out of order. Must stay below the grandTotal calculation above. */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    // Reuse: the parent's ID, else this user's stored draft, else a new one.
    if (!draftIdRef.current) {
      draftIdRef.current =
        bookingIdProp || readDraftId(user.uid) || newBookingId();
      saveDraftId(user.uid, draftIdRef.current);
    }

    setIsCreatingDraft(true);
    setSaveError(null);

    saveChainRef.current = saveChainRef.current.then(async () => {
      try {
        const record = await buildBookingRecord();
        let created = await createBooking(record, {
          bookingId: draftIdRef.current,
        });

        // The remembered draft was already paid: start a fresh booking.
        if (
          created.paymentStatus === "paid" ||
          created.status === "confirmed"
        ) {
          draftIdRef.current = newBookingId();
          saveDraftId(user.uid, draftIdRef.current);
          created = await createBooking(record, {
            bookingId: draftIdRef.current,
          });
        }

        if (cancelled) return;
        setBookingId(created.id);
        setSavedTotal(record.grandTotal);
        onBookingCreated?.(created.id);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to save booking draft:", err);
        setSaveError(err?.message || t.errorBookingStart);
      } finally {
        if (!cancelled) setIsCreatingDraft(false);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, grandTotal, couponCode]);

  const handlePaid = async () => {
    // Payment done: the next purchase must start a fresh booking.
    if (user) clearDraftId(user.uid);

    let record;
    try {
      record = await buildBookingRecord();
    } catch (err) {
      console.error("Failed to finalize booking record after payment:", err);
      setSaveError(err?.message || t.errorPaymentFailed);
      return;
    }
    onConfirm({
      id: bookingId,
      ...record,
      paymentStatus: "paid",
      status: "confirmed",
    });
  };

  const handleApplyCoupon = async () => {
    const code = coupon.trim();
    if (!code) return;
    setCouponError(null);
    setCouponLoading(true);
    try {
      const found = await getCouponByCode(code);
      const invalidReason = validateCoupon(found);
      if (invalidReason) {
        setCouponError(invalidReason);
        setAppliedCoupon(null);
        return;
      }
      setAppliedCoupon(found);
    } catch (err) {
      console.error("Failed to apply coupon:", err);
      setCouponError("Something went wrong applying this coupon. Try again.");
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCoupon("");
    setCouponError(null);
  };

  return (
    <div className="flex flex-col pb-28 mt-6 px-6 sm:px-0 max-w-2xl mx-auto">
      {/* Back + title + Package Summary — pinned below the navbar as one block */}
      <div className="sticky top-20 z-10 bg-white pb-3 pt-1 -mt-1">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#0B2421] transition-colors mb-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
        )}
        <div className="pt-2 pb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">{t.title}</h1>
          <p className="text-sm text-gray-500">{t.subtitle}</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 shadow-sm">
          <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-3">
            Package
          </p>
          <p className="font-bold text-gray-800">
            {selectedPackage?.rounds} Round
            {selectedPackage?.rounds > 1 ? "s" : ""} / {selectedPackage?.nights}{" "}
            Night{nights > 1 ? "s" : ""} Package
          </p>
          {startDate && endDate && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500">
              <CalendarIcon className="h-3.5 w-3.5 text-emerald-700 flex-shrink-0" />
              <span>
                {format(parseDateOnly(startDate), "dd MMM yyyy")} –{" "}
                {format(parseDateOnly(endDate), "dd MMM yyyy")}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4 mt-4">
        {/* Golf Courses */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">
              {t.sectionGolfRounds}
            </p>
            <p className="text-sm font-bold text-gray-800">
              SGD {golfTotal} total
            </p>
          </div>
          <div className="space-y-3">
            {selectedCourses.map((course, i) => (
              <div key={i} className="flex items-center gap-3">
                <img
                  src={course.image}
                  alt={course.name}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 text-sm truncate">
                    {course.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    Round {i + 1} · {course.city}, {course.country}
                  </p>
                  {roundDates?.[i] && (
                    <div className="flex items-center gap-1 mt-0.5 text-xs text-emerald-800">
                      <CalendarIcon className="h-3 w-3 flex-shrink-0" />
                      <span>
                        {format(parseDateOnly(roundDates[i]), "dd MMM yyyy")}
                        {roundTimes?.[i] ? ` · ${roundTimes[i]} WIB` : ""}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                    <Users className="h-3 w-3 flex-shrink-0" />
                    <span>
                      {players} player{players > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                {roundPrices[i] != null && (
                  <p className="text-sm font-bold text-gray-800 shrink-0">
                    SGD {roundPrices[i]}
                  </p>
                )}
              </div>
            ))}
          </div>
          {golfFootnote?.trim() && (
            <p className="text-xs text-gray-500 italic mt-3 pt-3 border-t border-gray-200 whitespace-pre-line">
              {golfFootnote}
            </p>
          )}
        </div>

        {/* Golfer recap — read-only, with a link back to edit */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">
              Golfer Details
            </p>
            {onEditGolfers && (
              <button
                type="button"
                onClick={onEditGolfers}
                className="flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900"
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
            )}
          </div>
          <div className="space-y-2">
            {(golfers || []).map((g, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-gray-800">{g.name}</span>
                {g.isLead && (
                  <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-100 rounded-full px-2 py-0.5">
                    Lead Golfer
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Ferry */}
        {selectedFerry && (
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-3">
              {t.sectionFerry}
            </p>
            <div className="flex items-center gap-3">
              <img
                src={selectedFerry.image}
                alt={selectedFerry.title}
                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 text-sm truncate">
                  {selectedFerry.title}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {selectedFerry.originport} ⇄ {selectedFerry.destination}
                </p>
                {selectedFerryTime && (
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-emerald-800">
                    <CalendarIcon className="h-3 w-3 flex-shrink-0" />
                    <span>
                      Inbound{" "}
                      {startDate
                        ? format(parseDateOnly(startDate), "dd MMM") + " · "
                        : ""}
                      departs {selectedFerryTime}
                    </span>
                  </div>
                )}
                {selectedOutboundFerryTime && (
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-emerald-800">
                    <CalendarIcon className="h-3 w-3 flex-shrink-0" />
                    <span>
                      Outbound{" "}
                      {endDate
                        ? format(parseDateOnly(endDate), "dd MMM") + " · "
                        : ""}
                      departs {selectedOutboundFerryTime}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-sm font-bold text-gray-800">SGD {ferryTotal}</p>
            </div>
          </div>
        )}

        {/* Land Transport */}
        {selectedTransport && (
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-3">
              {t.sectionLandTransport}
            </p>
            <div className="flex items-center gap-3">
              <img
                src={selectedTransport.image}
                alt={selectedTransport.title}
                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 text-sm truncate">
                  {selectedTransport.title}
                  {selectedTransport.type ? ` · ${selectedTransport.type}` : ""}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {selectedTransport.originport} ⇄{" "}
                  {selectedTransport.destination}
                </p>
                {selectedTransportTime && (
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-emerald-800">
                    <CalendarIcon className="h-3 w-3 flex-shrink-0" />
                    <span>Inbound pickup {selectedTransportTime}</span>
                  </div>
                )}
                {selectedOutboundTransportTime && (
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-emerald-800">
                    <CalendarIcon className="h-3 w-3 flex-shrink-0" />
                    <span>Return pickup {selectedOutboundTransportTime}</span>
                  </div>
                )}
              </div>
              <p className="text-sm font-bold text-gray-800">
                SGD {transportTotal}
              </p>
            </div>
          </div>
        )}

        {/* Accommodation */}
        {selectedAccommodation && (
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-3">
              {t.sectionAccommodation}
            </p>
            <div className="flex items-center gap-3">
              <img
                src={selectedAccommodation.image}
                alt={selectedAccommodation.title}
                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 text-sm truncate">
                  {selectedAccommodation.title}
                </p>
                <p className="text-xs text-gray-500">
                  {selectedAccommodation.destination} · {nights} night
                  {nights > 1 ? "s" : ""} · {rooms} Single Room
                  {rooms > 1 ? "s" : ""} ({selectedAccommodation.bed_type || "King Bed"})
                </p>
                {checkInDate && checkOutDate && (
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-emerald-800">
                    <CalendarIcon className="h-3 w-3 flex-shrink-0" />
                    <span>
                      {format(parseDateOnly(checkInDate), "dd MMM")} –{" "}
                      {format(parseDateOnly(checkOutDate), "dd MMM yyyy")}
                    </span>
                  </div>
                )}
                {checkInTime && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Check-in {checkInTime} (fixed)
                  </p>
                )}
              </div>
              <p className="text-sm font-bold text-gray-800">
                SGD {accommodationTotal}
              </p>
            </div>
          </div>
        )}

        {/* Coupon */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="overflow-hidden">
              {appliedCoupon ? (
                <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">
                      {appliedCoupon.code} applied
                    </p>
                    <p className="text-xs text-emerald-700">
                      {appliedCoupon.discountPercent}% off{" "}
                      {appliedCoupon.scope === "global"
                        ? "your total booking"
                        : appliedCoupon.scope}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={coupon}
                        onChange={(e) => setCoupon(e.target.value)}
                        placeholder="Enter coupon code"
                        className="w-full h-11 rounded-lg border border-gray-200 bg-white px-4 text-sm outline-none transition-all duration-200 focus:border-green-500 focus:ring-4 focus:ring-blue-100"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={!coupon.trim() || couponLoading}
                      onClick={handleApplyCoupon}
                      className="h-11 px-5 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-95 disabled:bg-gray-300 disabled:cursor-not-allowed text-base bg-emerald-800 hover:bg-emerald-900 text-white flex items-center gap-2"
                    >
                      {couponLoading && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      Apply
                    </button>
                  </div>
                  {couponError ? (
                    <p className="text-xs text-red-600 mt-2 px-1">
                      {couponError}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 mt-2 px-1">
                      Enter a valid promotional code to get a discount.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Price Breakdown */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
          <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-3">
            Price Breakdown
          </p>
          <div className="flex justify-between text-sm text-gray-600">
            <span>
              Golf ({selectedCourses.length} round
              {selectedCourses.length > 1 ? "s" : ""})
            </span>
            <span>SGD {golfTotal}</span>
          </div>
          {selectedFerry && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>
                Ferry (round trip, {players} passenger{players > 1 ? "s" : ""})
              </span>
              <span>SGD {ferryTotal}</span>
            </div>
          )}
          {selectedTransport && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>{t.summaryLandTransportLabel}</span>
              <span>SGD {transportTotal}</span>
            </div>
          )}
          {selectedAccommodation && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>
                {t.sectionAccommodation} ({nights} night{nights > 1 ? "s" : ""}{" "}
                × {rooms} room{rooms > 1 ? "s" : ""})
              </span>
              <span>SGD {accommodationTotal}</span>
            </div>
          )}

          {appliedCoupon && discountAmount > 0 && (
            <div className="flex justify-between text-sm text-emerald-700 font-medium">
              <span>Coupon ({appliedCoupon.code})</span>
              <span>−SGD {discountAmount}</span>
            </div>
          )}

          <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-gray-900">
            <span>{t.totalLabel}</span>
            <span>SGD {grandTotal}</span>
          </div>
        </div>

        {saveError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
            {saveError}
          </div>
        )}

        {/* Payment — Stripe (Card + PayNow). Mounts only once the saved draft
            matches the total on screen, so the charge is always up to date. */}
        {isCreatingDraft && (
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-500">{t.preparingBooking}</p>
          </div>
        )}
        {bookingId && !isCreatingDraft && savedTotal === grandTotal && (
          <StripePaymentSection
            key={savedTotal}
            bookingId={bookingId}
            amount={grandTotal}
            customerEmail={auth.currentUser?.email}
            customerName={auth.currentUser?.displayName}
            onPaid={handlePaid}
            onError={(err) =>
              setSaveError(err?.message || t.errorPaymentFailed)
            }
          />
        )}
      </div>

      {/* Sticky Bottom Bar — summary only; the pay button lives inside
          StripePaymentSection. */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between z-20">
        <div>
          <p className="text-xs text-gray-500">{t.totalLabel}</p>
          <p className="text-2xl font-bold text-gray-900">SGD {grandTotal}</p>
        </div>
      </div>

      <AuthGateModal
        open={showAuthGate}
        onClose={() => setShowAuthGate(false)}
        onAuthenticated={() => setShowAuthGate(false)}
      />
    </div>
  );
}
